import { appCodeHost } from '@utils/appDomain'

const SCRIPT_ID = 'chatx-site-support-widget'

function siteSupportWidgetSrc(): string {
  const id = (typeof __SITE_SUPPORT_WIDGET_ID__ !== 'undefined' ? __SITE_SUPPORT_WIDGET_ID__ : '').trim()
  const host = appCodeHost()
  if (!id || !host) return ''
  return `//${host}/widget/${encodeURIComponent(id)}`
}

type WidgetApi = {
  destroy?: () => void
}

type WidgetWindow = Window & {
  SupportWidget?: WidgetApi
  __SUPPORT_WIDGET_LOADED?: boolean
}

let wanted = false
let inflight = false

function widgetWindow(): WidgetWindow {
  return window as WidgetWindow
}

function removeWidgetDom() {
  document.getElementById('support-widget-container')?.remove()
  document.getElementById('support-widget-iframe')?.remove()
  document.getElementById(SCRIPT_ID)?.remove()
}

/**
 * Temporarily hide the support bubble (e.g. while the full-screen mobile menu
 * is open — the launcher sits above the menu and covers its bottom buttons).
 * The loader manages the iframe's own `visibility`, so toggle the container.
 */
export function setSiteSupportWidgetHidden(hidden: boolean) {
  const container = document.getElementById('support-widget-container')
  if (!container) return
  container.style.visibility = hidden ? 'hidden' : ''
}

/** Tear down the embedded support bubble so it does not follow SPA navigation. */
export function hideSiteSupportWidget() {
  const w = widgetWindow()
  try {
    w.SupportWidget?.destroy?.()
  } catch {
    /* loader may already be gone */
  }
  w.__SUPPORT_WIDGET_LOADED = false
  removeWidgetDom()
}

/**
 * Show the ChatX site widget only while `visible` is true (public site pages, see App.tsx).
 * The loader is a one-shot script, so leaving those pages destroys it
 * and coming back injects it again.
 */
export function syncSiteSupportWidget(visible: boolean) {
  wanted = visible
  if (!visible) {
    if (!inflight) hideSiteSupportWidget()
    return
  }

  const w = widgetWindow()
  if (w.__SUPPORT_WIDGET_LOADED && document.getElementById('support-widget-container')) return
  if (inflight) return

  const src = siteSupportWidgetSrc()
  if (!src) return

  inflight = true
  const script = document.createElement('script')
  script.id = SCRIPT_ID
  script.src = src
  script.async = true
  const finish = () => {
    inflight = false
    if (!wanted) hideSiteSupportWidget()
  }
  script.onload = finish
  script.onerror = finish
  document.body.appendChild(script)
}
